import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { AdminGuard, AdminShell } from "./AdminShell";
import { authHeaders } from "../../context/AuthContext";
import { useSeo } from "../../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  category: "general",
  tags: "",
  author: "Wald Team",
  meta_title: "",
  meta_description: "",
  og_image: "",
  published: true,
};

export default function AdminInsightsPage() {
  useSeo({ title: "Admin · Insights", description: "Manage insights.", path: "/admin" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/insights`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(emptyForm);
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({
      title: item.title,
      slug: item.slug,
      excerpt: item.excerpt,
      body: item.body,
      category: item.category || "general",
      tags: (item.tags || []).join(", "),
      author: item.author || "Wald Team",
      meta_title: item.meta_title || "",
      meta_description: item.meta_description || "",
      og_image: item.og_image || "",
      published: !!item.published,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      slug: form.slug.trim() || undefined,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      og_image: form.og_image || null,
    };
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/insights`, payload, {
          withCredentials: true,
          headers: authHeaders(),
        });
        toast.success("Insight created");
      } else {
        await axios.put(`${API}/admin/insights/${editing}`, payload, {
          withCredentials: true,
          headers: authHeaders(),
        });
        toast.success("Insight updated");
      }
      cancel();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this insight?")) return;
    try {
      await axios.delete(`${API}/admin/insights/${id}`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <AdminGuard>
      <Toaster position="top-right" richColors />
      <AdminShell>
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow mb-2">Content</div>
            <h1 className="font-display text-3xl tracking-tight">Insights</h1>
          </div>
          <button
            onClick={startNew}
            data-testid="admin-new-insight"
            className="btn-copper"
          >
            <Plus size={14} strokeWidth={2} /> New insight
          </button>
        </div>

        {editing && (
          <form
            onSubmit={save}
            data-testid="admin-insight-form"
            className="border border-border bg-card p-6 mb-10 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-lg">{editing === "new" ? "New insight" : "Edit insight"}</div>
              <button type="button" onClick={cancel} className="opacity-60 hover:opacity-100" aria-label="close">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AdminField label="Title" required>
                <input required data-testid="ai-title" className="wald-admin-input"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </AdminField>
              <AdminField label="Slug (optional)">
                <input data-testid="ai-slug" className="wald-admin-input" placeholder="auto-from-title"
                  value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </AdminField>
              <AdminField label="Category">
                <input data-testid="ai-category" className="wald-admin-input"
                  value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </AdminField>
              <AdminField label="Tags (comma separated)">
                <input data-testid="ai-tags" className="wald-admin-input"
                  value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              </AdminField>
              <AdminField label="Author">
                <input data-testid="ai-author" className="wald-admin-input"
                  value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
              </AdminField>
              <AdminField label="OG Image URL (optional)">
                <input data-testid="ai-ogimage" className="wald-admin-input"
                  value={form.og_image} onChange={(e) => setForm({ ...form, og_image: e.target.value })} />
              </AdminField>
            </div>
            <AdminField label="Excerpt" required>
              <textarea required rows={2} data-testid="ai-excerpt" className="wald-admin-input resize-y"
                value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </AdminField>
            <AdminField label="Body (double-newline for new paragraph)" required>
              <textarea required rows={10} data-testid="ai-body" className="wald-admin-input font-mono-tab text-sm resize-y"
                value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </AdminField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AdminField label="Meta title (SEO)">
                <input data-testid="ai-metatitle" className="wald-admin-input"
                  value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} />
              </AdminField>
              <AdminField label="Meta description (SEO)">
                <input data-testid="ai-metadesc" className="wald-admin-input"
                  value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
              </AdminField>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="ai-published"
                checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="accent-[color:var(--wald-copper)]" />
              Published
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="ai-save" disabled={saving} className="btn-copper disabled:opacity-70">
                {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save</>}
              </button>
              <button type="button" onClick={cancel} className="btn-outline">Cancel</button>
            </div>
            <style>{`.wald-admin-input{width:100%;background:hsl(var(--background));border:1px solid hsl(var(--border));color:hsl(var(--foreground));padding:.55rem .75rem;font-size:.875rem;border-radius:2px;outline:none}.wald-admin-input:focus{border-color:var(--wald-copper);box-shadow:0 0 0 3px rgba(214,90,28,.15)}`}</style>
          </form>
        )}

        {loading ? (
          <div className="opacity-60 text-sm">Loading…</div>
        ) : (
          <div className="border border-border">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-border eyebrow">
              <div className="col-span-6">Title</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {items.length === 0 && (
              <div className="p-8 text-sm opacity-60">No insights yet. Create your first one.</div>
            )}
            {items.map((it) => (
              <div key={it.id} data-testid={`admin-insight-row-${it.slug}`} className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-b border-border last:border-b-0">
                <div className="col-span-6">
                  <div className="font-display text-base tracking-tight">{it.title}</div>
                  <div className="text-xs opacity-60 font-mono-tab">/{it.slug}</div>
                </div>
                <div className="col-span-2 text-sm opacity-80">{it.category}</div>
                <div className="col-span-2 text-xs">
                  <span className={`px-2 py-0.5 border ${it.published ? "border-[color:var(--wald-copper)] text-[color:var(--wald-copper)]" : "border-border opacity-60"}`}>
                    {it.published ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button data-testid={`admin-edit-${it.slug}`} onClick={() => startEdit(it)} className="p-2 border border-border hover:border-[color:var(--wald-copper)]" aria-label="edit"><Pencil size={13} /></button>
                  <button data-testid={`admin-delete-${it.slug}`} onClick={() => remove(it.id)} className="p-2 border border-border hover:border-[color:var(--wald-copper)]" aria-label="delete"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminShell>
    </AdminGuard>
  );
}

const AdminField = ({ label, required, children }) => (
  <label className="block">
    <div className="eyebrow mb-2">{label}{required && <span className="text-[color:var(--wald-copper)]"> *</span>}</div>
    {children}
  </label>
);
